import {
    addDays,
    endOfDay,
    endOfMonth,
    endOfWeek,
    format,
    isBefore,
    isSameDay,
    startOfDay,
    startOfMonth,
    startOfWeek,
} from 'date-fns';
import { Temporal } from 'temporal-polyfill';
import { DATE_FORMAT_ISO_DAY } from '../../constants/dateFormat';
import { CalendarGranularity, type CalendarPublication } from './types';

export const CALENDAR_WEEKDAY_HEADER_LABELS = [
    'Lun',
    'Mar',
    'Mer',
    'Jeu',
    'Ven',
    'Sam',
    'Dim',
] as const;

const WEEK_OPTIONS = { weekStartsOn: 1 } as const;
const HOURS_PER_DAY = 24;

export type HourSlot = Readonly<{ hour: number; minute: number; label: string }>;

function pad2(value: number): string {
    return String(value).padStart(2, '0');
}

function parsePublicationPlainDateTime(publicationDate: string): Temporal.PlainDateTime {
    return Temporal.PlainDateTime.from(publicationDate);
}

export function formatPublicationTime(publicationDate: string): string {
    const dt = parsePublicationPlainDateTime(publicationDate);
    return `${pad2(dt.hour)}:${pad2(dt.minute)}`;
}

export function publicationDayKey(publicationDate: string): string {
    const dt = parsePublicationPlainDateTime(publicationDate);
    return `${dt.year}-${pad2(dt.month)}-${pad2(dt.day)}`;
}

/** Index de ligne 0–23 pour une grille à pas d'une heure. */
export function publicationSlotIndex(publicationDate: string): number {
    return parsePublicationPlainDateTime(publicationDate).hour;
}

function comparePublications(a: CalendarPublication, b: CalendarPublication): number {
    return Temporal.PlainDateTime.compare(
        parsePublicationPlainDateTime(a.publicationDate),
        parsePublicationPlainDateTime(b.publicationDate),
    );
}

export function getCalendarRange(
    anchor: Date,
    view: CalendarGranularity,
): Readonly<{ from: Date; to: Date }> {
    if (view === CalendarGranularity.Day) {
        return { from: startOfDay(anchor), to: endOfDay(anchor) };
    }
    if (view === CalendarGranularity.Week) {
        return { from: startOfWeek(anchor, WEEK_OPTIONS), to: endOfWeek(anchor, WEEK_OPTIONS) };
    }
    return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
}

export const HOUR_SLOTS: readonly HourSlot[] = Array.from({ length: HOURS_PER_DAY }, (_, hour) => ({
    hour,
    minute: 0,
    label: `${pad2(hour)}:00`,
}));

export function publicationsForDay(
    publications: readonly CalendarPublication[],
    day: Date,
): readonly CalendarPublication[] {
    const dayKey = format(day, DATE_FORMAT_ISO_DAY);
    return publications
        .filter(p => publicationDayKey(p.publicationDate) === dayKey)
        .sort(comparePublications);
}

export function publicationsByDayKey(
    publications: readonly CalendarPublication[],
): ReadonlyMap<string, readonly CalendarPublication[]> {
    const byDay = new Map<string, CalendarPublication[]>();
    for (const publication of publications) {
        const key = publicationDayKey(publication.publicationDate);
        const existing = byDay.get(key);
        if (existing) {
            existing.push(publication);
        } else {
            byDay.set(key, [publication]);
        }
    }
    for (const list of byDay.values()) {
        list.sort(comparePublications);
    }
    return byDay;
}

export function monthGridDays(anchor: Date): readonly Date[] {
    const gridStart = startOfWeek(startOfMonth(anchor), WEEK_OPTIONS);
    const gridEnd = endOfWeek(endOfMonth(anchor), WEEK_OPTIONS);
    const days: Date[] = [];
    for (let day = gridStart; day <= gridEnd; day = addDays(day, 1)) {
        days.push(day);
    }
    return days;
}

export function weekDays(anchor: Date): readonly Date[] {
    const start = startOfWeek(anchor, WEEK_OPTIONS);
    return Array.from({ length: 7 }, (_, index) => addDays(start, index));
}

export function collectCalendarLayoutDateKeys(
    anchor: Date,
    granularity: CalendarGranularity,
): readonly string[] {
    if (granularity === CalendarGranularity.Month) {
        return monthGridDays(anchor).map(day => format(day, DATE_FORMAT_ISO_DAY));
    }
    if (granularity === CalendarGranularity.Week) {
        return weekDays(anchor).map(day => format(day, DATE_FORMAT_ISO_DAY));
    }
    return [format(anchor, DATE_FORMAT_ISO_DAY)];
}

export function isPastCalendarDay(day: Date): boolean {
    return isBefore(startOfDay(day), startOfDay(new Date()));
}

export type CalendarPresetSchedule = Readonly<{
    date: string;
    hour: string;
    minute: string;
}>;

export type DayViewCreateSlotState = Readonly<{
    showCreateButton: boolean;
    presetSchedule: CalendarPresetSchedule;
}>;

const HALF_HOUR_MINUTE = 30;

function calendarPresetSchedule(
    dayKey: string,
    hour: number,
    minute: number,
): CalendarPresetSchedule {
    return {
        date: dayKey,
        hour: pad2(hour),
        minute: pad2(minute),
    };
}

export function getDayViewCreateSlotState(
    day: Date,
    dayKey: string,
    slotHour: number,
    now: Date,
): DayViewCreateSlotState {
    const presetSchedule = calendarPresetSchedule(dayKey, slotHour, 0);

    if (isPastCalendarDay(day)) {
        return { showCreateButton: false, presetSchedule };
    }

    if (!isSameDay(startOfDay(day), startOfDay(now))) {
        return { showCreateButton: true, presetSchedule };
    }

    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    if (slotHour < currentHour) {
        return { showCreateButton: false, presetSchedule };
    }

    if (slotHour > currentHour) {
        return { showCreateButton: true, presetSchedule };
    }

    const presetAtHalfHour = calendarPresetSchedule(dayKey, slotHour, HALF_HOUR_MINUTE);

    if (currentMinute >= HALF_HOUR_MINUTE) {
        return { showCreateButton: false, presetSchedule: presetAtHalfHour };
    }

    return { showCreateButton: true, presetSchedule: presetAtHalfHour };
}
